import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Divider, IconButton, MenuItem, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { AGGREGATIONS, AQI_CHANNELS, COMPARATORS, emptyBandRow, emptyConditionRow } from './expressionBuilder';
import { channelUnit } from './channelOptions';
import { bandStyle } from './ruleBands';

/**
 * Guided rule building: dropdown rows, or an ordered band table.
 *
 * Produces a DSL expression through expressionBuilder and nothing else — there is
 * one stored form of a rule and this is a view of it, not a second format.
 */

SimpleRuleEditor.propTypes = {
  config: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  channels: PropTypes.array.isRequired,
};

const UNITS = [
  { value: 's', label: 'seconds' },
  { value: 'm', label: 'minutes' },
  { value: 'h', label: 'hours' },
];

function SimpleRuleEditor({ config, onChange, channels }) {
  const set = (patch) => onChange({ ...config, ...patch });
  const setRow = (index, patch) =>
    set({ rows: config.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });

  const isBands = config.mode === 'bands';

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={config.mode}
        onChange={(e, v) => {
          if (!v) return;
          set(
            v === 'bands'
              ? {
                  mode: 'bands',
                  term: config.term ?? { ...emptyConditionRow(), channel: config.rows?.[0]?.channel ?? '' },
                  rows: [emptyBandRow('Good'), { label: 'Bad', catchAll: true }],
                }
              : { mode: 'conditions', joiner: 'and', rows: [emptyConditionRow()] }
          );
        }}
      >
        <ToggleButton value="conditions">Break / not broken</ToggleButton>
        <ToggleButton value="bands">Graded bands</ToggleButton>
      </ToggleButtonGroup>

      {isBands ? (
        <BandEditor config={config} set={set} setRow={setRow} channels={channels} />
      ) : (
        <ConditionEditor config={config} set={set} setRow={setRow} channels={channels} />
      )}

      <Divider />

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <DurationField
          label="Only if it lasts"
          hint="Ignores brief spikes. Leave blank to break immediately."
          value={config.sustain}
          onChange={(sustain) => set({ sustain })}
        />
        <DurationField
          label="Clear after"
          hint="How long it must stay back in range before the violation ends. Defaults to 5 seconds."
          value={config.settle}
          onChange={(settle) => set({ settle })}
        />
      </Stack>
    </Stack>
  );
}

function TermFields({ row, onChange, channels }) {
  const aggregation = AGGREGATIONS.find((a) => a.value === row.aggregation) ?? AGGREGATIONS[0];
  // aqi() is only defined for the pollutants with an EPA breakpoint table.
  const options = row.aggregation === 'aqi'
    ? channels.filter((c) => AQI_CHANNELS.includes(c.groupKey))
    : channels;

  return (
    <>
      <TextField
        select
        size="small"
        label="Measurement"
        value={options.some((c) => c.groupKey === row.channel) ? row.channel : ''}
        onChange={(e) => onChange({ channel: e.target.value })}
        sx={{ minWidth: 190 }}
      >
        {options.map((c) => (
          <MenuItem key={c.groupKey} value={c.groupKey}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Using"
        value={row.aggregation}
        onChange={(e) => {
          const next = e.target.value;
          // Switching to AQI can strand a channel it is not defined for; clear it
          // rather than leaving a selection that will not validate.
          const keepChannel = next !== 'aqi' || AQI_CHANNELS.includes(row.channel);
          onChange({ aggregation: next, channel: keepChannel ? row.channel : '' });
        }}
        sx={{ minWidth: 170 }}
      >
        {AGGREGATIONS.map((a) => (
          <MenuItem key={a.value} value={a.value}>
            {a.label}
          </MenuItem>
        ))}
      </TextField>

      {aggregation.windowed && (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="number"
            label="Window"
            value={row.window?.amount ?? ''}
            onChange={(e) => onChange({ window: { ...row.window, amount: e.target.value } })}
            sx={{ width: 90 }}
          />
          <TextField
            select
            size="small"
            label="Unit"
            value={row.window?.unit ?? 'm'}
            onChange={(e) => onChange({ window: { ...row.window, unit: e.target.value } })}
            sx={{ width: 110 }}
          >
            {UNITS.map((u) => (
              <MenuItem key={u.value} value={u.value}>
                {u.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
    </>
  );
}

function ConditionEditor({ config, set, setRow, channels }) {
  return (
    <Stack spacing={1.5}>
      {config.rows.map((row, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {index > 0 && (
            <TextField
              select
              size="small"
              value={config.joiner ?? 'and'}
              onChange={(e) => set({ joiner: e.target.value })}
              sx={{ width: 90 }}
            >
              {/* One joiner for the whole rule: the language rejects mixing them,
                  because the precedence needed to disambiguate is not something
                  these rows could draw. */}
              <MenuItem value="and">and</MenuItem>
              <MenuItem value="or">or</MenuItem>
            </TextField>
          )}
          <TermFields row={row} channels={channels} onChange={(patch) => setRow(index, patch)} />
          <TextField
            select
            size="small"
            label="Is"
            value={row.comparator}
            onChange={(e) => setRow(index, { comparator: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            {COMPARATORS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Value"
            value={row.value}
            onChange={(e) => setRow(index, { value: e.target.value })}
            InputProps={{ endAdornment: <UnitAdornment groupKey={row.channel} /> }}
            sx={{ width: 130 }}
          />
          {config.rows.length > 1 && (
            <IconButton
              size="small"
              aria-label="Remove condition"
              onClick={() => set({ rows: config.rows.filter((_, i) => i !== index) })}
            >
              <Iconify icon="eva:close-outline" width={18} />
            </IconButton>
          )}
        </Stack>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-outline" />}
          onClick={() => set({ rows: [...config.rows, emptyConditionRow()] })}
        >
          Add condition
        </Button>
      </Box>
    </Stack>
  );
}

function BandEditor({ config, set, setRow, channels }) {
  const rows = config.rows ?? [];

  const move = (index, delta) => {
    const target = index + delta;
    // The catch-all must stay last: anything after it is unreachable.
    if (target < 0 || target >= rows.length - 1 || index >= rows.length - 1) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    set({ rows: next });
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ mr: 1 }}>
          Grade
        </Typography>
        <TermFields
          row={config.term}
          channels={channels}
          onChange={(patch) => set({ term: { ...config.term, ...patch } })}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Bands are checked top to bottom and the first match wins, so they run from
        best to worst and only need an upper bound. The last band catches everything else.
      </Typography>

      {rows.map((row, index) => {
        const last = index === rows.length - 1;
        return (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 14, height: 28, borderRadius: 0.5, flexShrink: 0,
                bgcolor: bandStyle(index).color,
              }}
            />
            <TextField
              size="small"
              label={`Band ${index + 1}`}
              value={row.label}
              onChange={(e) => setRow(index, { label: e.target.value })}
              sx={{ minWidth: 200 }}
            />
            {last ? (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                everything above the bands before it
              </Typography>
            ) : (
              <>
                <TextField
                  select
                  size="small"
                  label="When"
                  value={row.comparator}
                  onChange={(e) => setRow(index, { comparator: e.target.value })}
                  sx={{ minWidth: 150 }}
                >
                  {COMPARATORS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Value"
                  value={row.value}
                  onChange={(e) => setRow(index, { value: e.target.value })}
                  InputProps={{ endAdornment: <UnitAdornment groupKey={config.term?.channel} /> }}
                  sx={{ width: 130 }}
                />
                <Tooltip title="Move up (less severe)">
                  <span>
                    <IconButton size="small" aria-label="Move band up" disabled={index === 0} onClick={() => move(index, -1)}>
                      <Iconify icon="eva:arrow-upward-outline" width={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down (more severe)">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Move band down"
                      disabled={index >= rows.length - 2}
                      onClick={() => move(index, 1)}
                    >
                      <Iconify icon="eva:arrow-downward-outline" width={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                {rows.length > 2 && (
                  <IconButton
                    size="small"
                    aria-label="Remove band"
                    onClick={() => set({ rows: rows.filter((_, i) => i !== index) })}
                  >
                    <Iconify icon="eva:close-outline" width={18} />
                  </IconButton>
                )}
              </>
            )}
          </Stack>
        );
      })}

      <Box>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-outline" />}
          onClick={() => set({ rows: [...rows.slice(0, -1), emptyBandRow(''), rows[rows.length - 1]] })}
        >
          Add band
        </Button>
      </Box>
    </Stack>
  );
}

function DurationField({ label, hint, value, onChange }) {
  return (
    <Tooltip title={hint}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          type="number"
          label={label}
          value={value?.amount ?? ''}
          onChange={(e) =>
            onChange(e.target.value === '' ? null : { amount: e.target.value, unit: value?.unit ?? 'm' })
          }
          sx={{ width: 130 }}
        />
        <TextField
          select
          size="small"
          label="Unit"
          value={value?.unit ?? 'm'}
          disabled={!value}
          onChange={(e) => onChange({ amount: value?.amount ?? 1, unit: e.target.value })}
          sx={{ width: 110 }}
        >
          {UNITS.map((u) => (
            <MenuItem key={u.value} value={u.value}>
              {u.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Tooltip>
  );
}

function UnitAdornment({ groupKey }) {
  const unit = channelUnit(groupKey);
  if (!unit) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
      {unit}
    </Typography>
  );
}

const rowShape = PropTypes.shape({
  channel: PropTypes.string,
  aggregation: PropTypes.string,
  window: PropTypes.shape({ amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), unit: PropTypes.string }),
  comparator: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

const durationShape = PropTypes.shape({
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
});

const configShape = PropTypes.shape({
  mode: PropTypes.string,
  joiner: PropTypes.string,
  rows: PropTypes.array,
  term: rowShape,
  sustain: durationShape,
  settle: durationShape,
});

TermFields.propTypes = { row: rowShape, onChange: PropTypes.func.isRequired, channels: PropTypes.array.isRequired };
ConditionEditor.propTypes = {
  config: configShape.isRequired,
  set: PropTypes.func.isRequired,
  setRow: PropTypes.func.isRequired,
  channels: PropTypes.array.isRequired,
};
BandEditor.propTypes = ConditionEditor.propTypes;
DurationField.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  value: durationShape,
  onChange: PropTypes.func.isRequired,
};
UnitAdornment.propTypes = { groupKey: PropTypes.string };

export default SimpleRuleEditor;
